import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useEffect, useMemo, useState } from 'react';

import type { GenericOpportunityActivityDataType } from '@/api/attribution';
import type { AudienceOptionType } from '@/api/audience';
import { useCampaignOpportunityActivity } from '@/api/campaign';
import type {
  OpportunityOldType,
  OpportunityStatusOldType,
  OpportunityType,
} from '@/api/opportunity';
import { useVendorOpportunityActivity } from '@/api/vendor';
import {
  InstantSearchSortOrder,
  type QueryStateFilter,
  useInstantSearchState,
} from '@/components/InstantSearch';
import { getSolQueryParamsNewFromQueryState } from '@/components/InstantSearch/util/search-util';
import { usePageFilterContext } from '@/components/page';
import { Panel } from '@/components/panels';
import { Tabs, TabsList, TabsTrigger } from '@/components/Tabs';
import { getOpportunityDateRange } from '@/helper/opportunity-date-util';

import OpportunityAttributionOnlineTable from './OpportunityAttributionOnlineTable';

const NON_PAGINATED_RESULTS = 100;

type Props = {
  oppType: OpportunityStatusOldType;
  audience?: AudienceOptionType;
  opportunity?: OpportunityType | OpportunityOldType;
  defaultSort: {
    sortBy: string;
    sortOrder: InstantSearchSortOrder;
  };
  defaultFilters: QueryStateFilter[];
  startDate?: string;
  endDate?: string;
  isLoading?: boolean;
};

const OpportunityAttributionOnlineTableContainer = ({
  oppType,
  opportunity,
  defaultSort,
  defaultFilters,
  startDate,
  endDate,
  isLoading = false,
}: Props) => {
  const {
    pageFilters: { audience },
  } = usePageFilterContext();
  const { queryState, updateQueryState } = useInstantSearchState();
  const [breakdownObject, setBreakdownObject] = useState<'vendor' | 'campaign'>('vendor');
  const hasPagination = breakdownObject === 'campaign';

  const breakdownOptionsObject = useMemo<{ value: 'vendor' | 'campaign'; label: string }[]>(
    () => [
      { value: 'vendor', label: t`Vendor` },
      { value: 'campaign', label: t`Campaign` },
    ],
    [],
  );

  useEffect(() => {
    updateQueryState({
      ...defaultSort,
      filters: defaultFilters,
      page: 1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oppType]);

  const opportunityDateRange = getOpportunityDateRange(opportunity);

  const chartStartDate = startDate ?? opportunityDateRange.startDate;
  const chartEndDate = endDate ?? opportunityDateRange.endDate;

  const solQueryParams = getSolQueryParamsNewFromQueryState(queryState);

  if (!hasPagination && solQueryParams) {
    solQueryParams.page.limit = NON_PAGINATED_RESULTS;
    solQueryParams.page.offset = 0;
  }

  const {
    vendorActivity,
    totalResults: totalVendorResults,
    isLoading: isLoadingVendor,
    error: errorVendor,
  } = useVendorOpportunityActivity(
    breakdownObject === 'vendor' ? solQueryParams : undefined,
    chartStartDate,
    chartEndDate,
    audience?.id,
    opportunity?.id,
  );
  const {
    campaignActivity,
    totalResults: totalCampaignResults,
    isLoading: isLoadingCampaign,
    error: errorCampaign,
  } = useCampaignOpportunityActivity(
    breakdownObject === 'campaign' ? solQueryParams : undefined,
    chartStartDate,
    chartEndDate,
    audience?.id,
    opportunity?.id,
  );

  const data = useMemo<GenericOpportunityActivityDataType[] | undefined>(() => {
    return breakdownObject === 'vendor'
      ? vendorActivity?.map(({ vendor, ...rest }) => ({
          ...rest,
          id: vendor.id,
          name: `${vendor.name} - ${vendor.channel?.name}`,
        }))
      : campaignActivity?.map(({ ad, ...rest }) => ({
          ...rest,
          id: ad.id,
          name: ad.name,
        }));
  }, [vendorActivity, campaignActivity, breakdownObject]);

  const sortedData = useMemo(() => {
    // Vendor needs to be sorted by channel AND vendor. We only sort by vendor name, so this
    // client-side sort will order channel names too.
    if (queryState.sortBy === 'name' && breakdownObject === 'vendor') {
      return data?.toSorted(
        (a, b) =>
          a.name.localeCompare(b.name) *
          (queryState.sortOrder === InstantSearchSortOrder.ASC ? 1 : -1),
      );
    }
    return data;
  }, [queryState.sortBy, queryState.sortOrder, data, breakdownObject]);

  const handleBreakdownChange = (value: string | null) => {
    if (value !== 'vendor' && value !== 'campaign') {
      return;
    }

    setBreakdownObject(value);
    updateQueryState({
      ...queryState,
      filters: defaultFilters,
      page: 1,
    });
  };

  const title =
    breakdownObject === 'vendor' ? t`Attribution by Vendor` : t`Attribution by Campaign`;

  return (
    <Panel
      title={title}
      infoPopover={{
        title,
        body: (
          <Trans>
            Attribution is shown for each attribution model on this table. This takes into account
            all interactions during each opportunity's timeline from the start of the Opportunity
            influence window to the opportunity creation date (for Pipeline) or closed date (for
            Closed Won).
          </Trans>
        ),
      }}
      actions={
        <Tabs value={breakdownObject} onValueChange={handleBreakdownChange}>
          <TabsList>
            {breakdownOptionsObject.map((option) => (
              <TabsTrigger key={option.value} value={option.value}>
                {option.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      }
    >
      <OpportunityAttributionOnlineTable
        oppType={oppType}
        breakdownObject={breakdownObject}
        data={sortedData}
        totalResults={
          breakdownObject === 'vendor'
            ? hasPagination
              ? totalVendorResults
              : 0
            : totalCampaignResults
        }
        isLoading={
          isLoading || (breakdownObject === 'vendor' ? isLoadingVendor : isLoadingCampaign)
        }
        error={breakdownObject === 'vendor' ? errorVendor : errorCampaign}
      />
    </Panel>
  );
};

export default OpportunityAttributionOnlineTableContainer;
